import type { ReactNode } from "react";
import { Card } from "../ui/card";
import { cn } from "../ui/utils";

/**
 * Tile shell for a dashboard chart: card chrome, a title row, and a slot for
 * per-tile actions (a headline figure, a menu, a legend toggle).
 *
 * Deliberately thin — every chart in ChartsComponent already owns its
 * loading, error and empty states, so this must not try to own them too.
 * It supplies the frame; the chart inside supplies the state machine.
 */
export interface ChartCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned slot in the title row. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function ChartCard({
  title,
  subtitle,
  actions,
  className,
  children,
}: ChartCardProps) {
  return (
    <Card className={cn("gap-3 p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-card-foreground">{title}</h3>
          {subtitle ? <div className="mt-1">{subtitle}</div> : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
      {children}
    </Card>
  );
}

/**
 * Chart title with a trailing pill, for tiles that show a bounded slice of a
 * larger set ("Top-5", "Last 30 days").
 */
export function chartTitleWithBadge(text: string, badge: string) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {text}
      {/* The pill is a label, not prose: without these the global
          break-anywhere wrap splits "Top-5" across two lines at the hyphen
          when the title row is tight, which also makes the card taller than
          its neighbours in the same grid row. */}
      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold normal-case text-primary">
        {badge}
      </span>
    </span>
  );
}
